
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Filter, CreditCard, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Subscription {
  id: string;
  first_name: string;
  last_name: string;
  subscription_plan: string;
  subscription_status: string;
  subscription_end_date: string;
  created_at: string;
  country: string;
  city: string;
}

const AdminSubscriptions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ['admin-subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('is_trainer', true)
        .not('subscription_plan', 'is', null)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Subscription[];
    }
  });

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'active': { label: 'Ativo', variant: 'default' as const, icon: CheckCircle },
      'trialing': { label: 'Teste', variant: 'secondary' as const, icon: Clock },
      'canceled': { label: 'Cancelado', variant: 'destructive' as const, icon: XCircle },
      'past_due': { label: 'Em Atraso', variant: 'destructive' as const, icon: XCircle },
      'incomplete': { label: 'Incompleto', variant: 'outline' as const, icon: Clock }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { 
      label: status, 
      variant: 'outline' as const, 
      icon: Clock 
    };
    
    return (
      <div className="flex items-center space-x-2">
        <statusInfo.icon className="h-4 w-4" />
        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      </div>
    );
  };

  const getPlanBadge = (plan: string) => {
    const planMap = {
      'free': { label: 'Grátis', variant: 'outline' as const },
      'trial': { label: 'Teste', variant: 'secondary' as const },
      'pro': { label: 'Pro', variant: 'default' as const },
      'premium': { label: 'Premium', variant: 'default' as const }
    };
    
    const planInfo = planMap[plan as keyof typeof planMap] || { 
      label: plan, 
      variant: 'outline' as const 
    };
    
    return <Badge variant={planInfo.variant}>{planInfo.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredSubscriptions = subscriptions?.filter(sub => {
    const matchesSearch = `${sub.first_name} ${sub.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sub.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sub.city?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || sub.subscription_status === statusFilter;
    const matchesPlan = planFilter === 'all' || sub.subscription_plan === planFilter;
    
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const activeCount = subscriptions?.filter(s => s.subscription_status === 'active').length || 0;
  const trialingCount = subscriptions?.filter(s => s.subscription_status === 'trialing').length || 0;
  const canceledCount = subscriptions?.filter(s => s.subscription_status === 'canceled').length || 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Gestão de Subscrições</h1>
        <p className="text-gray-600">Gerir todas as subscrições da plataforma</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <CreditCard className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{subscriptions?.length || 0}</div>
            <p className="text-xs text-gray-600">Subscrições totais</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ativas</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeCount}</div>
            <p className="text-xs text-gray-600">Subscrições ativas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Teste</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{trialingCount}</div>
            <p className="text-xs text-gray-600">Em período de teste</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Canceladas</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{canceledCount}</div>
            <p className="text-xs text-gray-600">Subscrições canceladas</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Pesquisar por nome ou localização..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Status</SelectItem>
            <SelectItem value="active">Ativo</SelectItem>
            <SelectItem value="trialing">Teste</SelectItem>
            <SelectItem value="canceled">Cancelado</SelectItem>
            <SelectItem value="past_due">Em Atraso</SelectItem>
          </SelectContent>
        </Select>

        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrar por plano" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Planos</SelectItem>
            <SelectItem value="free">Grátis</SelectItem>
            <SelectItem value="trial">Teste</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Subscrições</CardTitle>
          <CardDescription>
            {filteredSubscriptions?.length || 0} subscrições encontradas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utilizador</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Localização</TableHead>
                <TableHead>Data de Início</TableHead>
                <TableHead>Data de Fim</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubscriptions?.map((subscription) => (
                <TableRow key={subscription.id}>
                  <TableCell className="font-medium">
                    {subscription.first_name} {subscription.last_name}
                  </TableCell>
                  <TableCell>
                    {getPlanBadge(subscription.subscription_plan || 'free')}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(subscription.subscription_status || 'incomplete')}
                  </TableCell>
                  <TableCell>
                    {subscription.city && subscription.country 
                      ? `${subscription.city}, ${subscription.country}`
                      : subscription.country || 'Não informado'}
                  </TableCell>
                  <TableCell>
                    {formatDate(subscription.created_at)}
                  </TableCell>
                  <TableCell>
                    {subscription.subscription_end_date 
                      ? formatDate(subscription.subscription_end_date)
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm">
                      Ver Detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSubscriptions;
